import PropTypes from "prop-types";
import * as React from "react";

import { useShopOpenNotice } from "../../hooks";
import { resolveShopOpenNotice } from "../../services/shopStatus";
import { Alert, Button, Page } from "../../themeV2";
import { numberFormatter } from "../../util/formatNumber";
import css from "./ShopOpenNotice.module.css";

const count = (n) => numberFormatter.format(n);

export const suppressionNote = ({ optedOutCount, recentlyNotifiedCount }) =>
  [
    optedOutCount > 0 ? `${count(optedOutCount)} have opted out.` : "",
    recentlyNotifiedCount > 0
      ? `${count(
          recentlyNotifiedCount,
        )} were notified in the last 30 minutes and will be skipped.`
      : "",
  ]
    .filter(Boolean)
    .join(" ");

const ShopOpenNotice = ({ influencerId, shopName }) => {
  const notice = useShopOpenNotice(influencerId);
  const [pending, setPending] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const { visible, recipientCount, refresh } = notice;

  const resolve = React.useCallback(
    (notify) => {
      setPending(true);
      setFailed(false);

      resolveShopOpenNotice({ influencerId, notify })
        .then((result) => {
          if (result?.errors) {
            setFailed(true);
            return;
          }

          return refresh();
        })
        .finally(() => setPending(false));
    },
    [influencerId, refresh],
  );

  if (!visible) {
    return null;
  }

  const note = suppressionNote(notice);

  return (
    <Page.Section>
      <Alert
        severity="info"
        title={`${shopName} is open`}
        onClose={pending ? undefined : () => resolve(false)}
      >
        <span className={css.body}>
          {note && <span className={css.note}>{note}</span>}

          <Button
            data-test="shop-open-notice-notify"
            variant="filled"
            size="sm"
            loading={pending}
            onClick={() => resolve(true)}
          >
            Notify {count(recipientCount)} fans
          </Button>

          {failed && (
            <span className={css.error}>
              Something went wrong — please try again.
            </span>
          )}
        </span>
      </Alert>
    </Page.Section>
  );
};

ShopOpenNotice.propTypes = {
  influencerId: PropTypes.number,
  shopName: PropTypes.string,
};

export default ShopOpenNotice;
